import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2, Send, ArrowUpDown, ArrowUp, ArrowDown, Search, X, ChevronLeft, ChevronRight, Settings, Menu, Download } from 'lucide-react';
import { productsApi, brandsApi, suppliersApi, collectionsApi, tasksApi, factoriesApi } from '../services/api';
import BrandCollectionTree from '../components/BrandCollectionTree';

// Простой компонент загрузки
const LoadingBar = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 50%, #3b82f6 100%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    zIndex: 9999
  }} />
);

// Компонент фильтров - мемоизированный
const ProductsFilters = React.memo(({
  searchInput,
  onSearchChange,
  onClearSearch,
  filters,
  onFilterChange,
  onResetAll,
  suppliers,
  collections,
  factories,
  totalProducts
}) => {
  const [showExtraFilters, setShowExtraFilters] = useState(false);

  useEffect(() => {
    if (!showExtraFilters) return;
    const handler = (e) => {
      if (!e.target.closest('.extra-filters-container')) setShowExtraFilters(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExtraFilters]);

  const extraFiltersCount = [
    filters.has_price,
    filters.price_mismatch,
    filters.website_linked,
    filters.is_discontinued,
    filters.factory_in_stock,
  ].filter(v => v !== null).length;

  const sel = (value, onChange, options) => (
    <select value={value} onChange={onChange}
      style={{ width: '100%', height: '34px', border: '1px solid #e5e7eb', borderRadius: '6px', paddingLeft: '10px', fontSize: '13px', backgroundColor: '#fff', cursor: 'pointer' }}>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

        {/* Поиск */}
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
          <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Поиск по названию, артикулу, GUID..."
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ width: '100%', paddingLeft: '32px', paddingRight: searchInput ? '32px' : '10px', height: '38px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', backgroundColor: '#fff' }}
          />
          {searchInput && (
            <button onClick={onClearSearch} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px', display: 'flex' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* В наличии */}
        <label style={{ display: 'flex', height: '38px', alignItems: 'center', gap: '7px', cursor: 'pointer', padding: '0 12px', backgroundColor: '#fff', borderRadius: '8px', border: `1px solid ${filters.in_stock ? '#fcd34d' : '#e5e7eb'}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
          <input type="checkbox" checked={filters.in_stock}
            onChange={(e) => onFilterChange({ ...filters, in_stock: e.target.checked, supplier_id: e.target.checked ? filters.supplier_id : '' })}
            style={{ cursor: 'pointer', width: '15px', height: '15px' }} />
          <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>В наличии</span>
        </label>

        {/* Поставщик */}
        <select value={filters.supplier_id}
          onChange={(e) => onFilterChange({ ...filters, supplier_id: e.target.value, in_stock: e.target.value ? true : filters.in_stock })}
          style={{ height: '38px', border: `1px solid ${filters.supplier_id ? '#6366f1' : '#e5e7eb'}`, borderRadius: '8px', paddingLeft: '10px', paddingRight: '20px', fontSize: '13px', backgroundColor: '#fff', cursor: 'pointer', flexShrink: 0 }}>
          <option value="">Все поставщики</option>
          {suppliers?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        {/* Фабрика */}
        {factories?.length > 0 && (
          <select value={filters.factory_id}
            onChange={(e) => onFilterChange({ ...filters, factory_id: e.target.value })}
            style={{ height: '38px', border: `1px solid ${filters.factory_id ? '#6366f1' : '#e5e7eb'}`, borderRadius: '8px', paddingLeft: '10px', paddingRight: '20px', fontSize: '13px', backgroundColor: '#fff', cursor: 'pointer', flexShrink: 0 }}>
            <option value="">Все фабрики</option>
            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}

        {/* Кнопка доп. фильтров */}
        <div style={{ position: 'relative', flexShrink: 0 }} className="extra-filters-container">
          <button onClick={() => setShowExtraFilters(v => !v)}
            style={{
              height: '38px', padding: '0 12px',
              backgroundColor: extraFiltersCount > 0 ? '#eff6ff' : '#fff',
              border: `1px solid ${extraFiltersCount > 0 ? '#3b82f6' : '#e5e7eb'}`,
              borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', fontWeight: '500', color: extraFiltersCount > 0 ? '#1d4ed8' : '#374151', whiteSpace: 'nowrap',
            }}>
            Фильтры
            {extraFiltersCount > 0 && (
              <span style={{ background: '#3b82f6', color: '#fff', borderRadius: '999px', fontSize: '11px', fontWeight: '700', padding: '0 5px', lineHeight: '17px', minWidth: '17px', textAlign: 'center' }}>
                {extraFiltersCount}
              </span>
            )}
            <span style={{ fontSize: '9px', opacity: 0.5, marginLeft: '1px' }}>{showExtraFilters ? '▲' : '▼'}</span>
          </button>

          {showExtraFilters && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1200,
              backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.1)', padding: '14px', minWidth: '230px',
              display: 'flex', flexDirection: 'column', gap: '8px',
            }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '2px' }}>Доп. фильтры</div>

              {sel(
                filters.has_price === null ? '' : filters.has_price ? 'yes' : 'no',
                e => onFilterChange({ ...filters, has_price: e.target.value === '' ? null : e.target.value === 'yes' }),
                [['', 'Цена: все'], ['yes', 'Цена: есть'], ['no', 'Цена: нет']]
              )}
              {sel(
                filters.price_mismatch === null ? '' : filters.price_mismatch ? 'yes' : 'no',
                e => onFilterChange({ ...filters, price_mismatch: e.target.value === '' ? null : e.target.value === 'yes' }),
                [['', 'Расхождение цены: все'], ['yes', 'Расходится'], ['no', 'Совпадает']]
              )}
              {sel(
                filters.website_linked === null ? '' : filters.website_linked ? 'yes' : 'no',
                e => onFilterChange({ ...filters, website_linked: e.target.value === '' ? null : e.target.value === 'yes' }),
                [['', 'На сайте: все'], ['yes', 'На сайте: да'], ['no', 'На сайте: нет']]
              )}
              {sel(
                filters.is_discontinued === null ? '' : filters.is_discontinued ? 'yes' : 'no',
                e => onFilterChange({ ...filters, is_discontinued: e.target.value === '' ? null : e.target.value === 'yes' }),
                [['', 'Снято с пр-ва: все'], ['yes', 'Снято: да'], ['no', 'Снято: нет']]
              )}
              {sel(
                filters.factory_in_stock === null ? '' : filters.factory_in_stock ? 'yes' : 'no',
                e => onFilterChange({ ...filters, factory_in_stock: e.target.value === '' ? null : e.target.value === 'yes' }),
                [['', 'На фабрике: все'], ['yes', 'На фабрике: есть'], ['no', 'На фабрике: нет']]
              )}

              {extraFiltersCount > 0 && (
                <button onClick={() => onFilterChange({ ...filters, has_price: null, price_mismatch: null, website_linked: null, is_discontinued: null, factory_in_stock: null })}
                  style={{ height: '30px', background: 'none', border: '1px solid #fca5a5', borderRadius: '6px', color: '#ef4444', fontSize: '12px', cursor: 'pointer', fontWeight: '500', marginTop: '2px' }}>
                  Сбросить доп. фильтры
                </button>
              )}
            </div>
          )}
        </div>

        {/* Сбросить всё */}
        <button onClick={onResetAll}
          style={{ height: '38px', padding: '0 12px', background: '#fff', border: '1px solid #fca5a5', borderRadius: '8px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap', flexShrink: 0 }}>
          <X size={13} />
          Сбросить
        </button>

        {/* Счётчик */}
        <div style={{ fontSize: '13px', color: '#6b7280', padding: '0 12px', height: '38px', display: 'flex', alignItems: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
          Найдено: <span style={{ fontWeight: '600', color: '#374151', marginLeft: '4px' }}>{totalProducts}</span>
        </div>
      </div>
    </div>
  );
});

const ProductsPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('productsPageSize');
    return saved ? parseInt(saved, 10) : 50;
  });
  
  // Восстанавливаем поиск из localStorage
  const [searchInput, setSearchInput] = useState(() => {
    const saved = localStorage.getItem('productsSearchInput');
    return saved || '';
  });
  
  // Восстанавливаем фильтры из localStorage
  const [filters, setFilters] = useState(() => {
    const defaults = {
      search: '',
      brand_id: '',
      collection_id: '',
      supplier_id: '',
      factory_id: '',
      in_stock: false,
      not_synced_to_website: false,
      has_price: null,
      price_mismatch: null,
      website_linked: null,
      is_discontinued: null,
      factory_in_stock: null,
    };
    const saved = localStorage.getItem('productsFilters');
    if (saved) {
      try {
        return { ...defaults, ...JSON.parse(saved) };
      } catch (e) {
        console.error('Error parsing saved filters:', e);
      }
    }
    return defaults;
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null); // Для модального окна
  const [editValues, setEditValues] = useState(null); // Редактируемые значения в модалке
  const [showWebsiteImage, setShowWebsiteImage] = useState(false);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  
  // Восстанавливаем сортировку из localStorage
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('productsSortConfig');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved sort config:', e);
      }
    }
    return { key: null, direction: 'asc' };
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('productsVisibleColumns');
    const defaults = {
      sku: true,
      name: true,
      brand_name: true,
      collection_name: true,
      price: true,
      total_stock: true,
      created_at: true,
      last_stock_updated: true,
      last_stock_website_sync: true,
      last_price_updated: false,
      last_price_website_sync: false,
      website_linked: true,
      is_discontinued: true,
      factory_in_stock: false,
    };
    if (!saved) return defaults;
    const parsed = JSON.parse(saved);
    // Удаляем устаревшие ключи (например, supplier)
    const validKeys = Object.keys(defaults);
    const cleaned = {};
    validKeys.forEach(k => { cleaned[k] = parsed[k] !== undefined ? parsed[k] : defaults[k]; });
    return cleaned;
  });

  // Состояние для управления видимостью боковой панели
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('productsSidebarOpen');
    return saved ? JSON.parse(saved) : false;
  });

  // Порядок колонок
  const [columnOrder, setColumnOrder] = useState(() => {
    const defaultOrder = [
      'sku',
      'name',
      'brand_name',
      'collection_name',
      'price',
      'total_stock',
      'created_at',
      'last_stock_updated',
      'last_stock_website_sync',
      'last_price_updated',
      'last_price_website_sync',
      'website_linked',
      'is_discontinued',
      'factory_in_stock',
    ];
    const saved = localStorage.getItem('productsColumnOrder');
    if (!saved) return defaultOrder;
    const parsed = JSON.parse(saved);
    // Фильтруем устаревшие ключи, добавляем новые которых нет в сохранённом порядке
    const filtered = parsed.filter(key => defaultOrder.includes(key));
    const missing = defaultOrder.filter(key => !filtered.includes(key));
    return [...filtered, ...missing];
  });

  // Состояние для перетаскивания колонок
  const [draggingColumn, setDraggingColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Сохраняем настройки колонок в localStorage
  useEffect(() => {
    localStorage.setItem('productsVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Сохраняем порядок колонок в localStorage
  useEffect(() => {
    localStorage.setItem('productsColumnOrder', JSON.stringify(columnOrder));
  }, [columnOrder]);

  // Сохраняем состояние боковой панели в localStorage
  useEffect(() => {
    localStorage.setItem('productsSidebarOpen', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // Сохраняем фильтры в localStorage
  useEffect(() => {
    localStorage.setItem('productsFilters', JSON.stringify(filters));
  }, [filters]);

  // Сохраняем поисковый запрос в localStorage
  useEffect(() => {
    localStorage.setItem('productsSearchInput', searchInput);
  }, [searchInput]);

  // Сохраняем сортировку в localStorage
  useEffect(() => {
    localStorage.setItem('productsSortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Сохраняем pageSize в localStorage
  useEffect(() => {
    localStorage.setItem('productsPageSize', String(pageSize));
  }, [pageSize]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback(async () => {
    try {
      // Запрашиваем все товары текущей выборки (без пагинации)
      const params = {
        skip: 0,
        limit: 10000,
        search: filters.search || undefined,
        brand_id: filters.brand_id || undefined,
        collection_id: filters.collection_id || undefined,
        supplier_id: filters.supplier_id || undefined,
        factory_id: filters.factory_id || undefined,
        in_stock: filters.in_stock || undefined,
        not_synced_to_website: filters.not_synced_to_website || undefined,
        has_price: filters.has_price !== null ? filters.has_price : undefined,
        price_mismatch: filters.price_mismatch !== null ? filters.price_mismatch : undefined,
        website_linked: filters.website_linked !== null ? filters.website_linked : undefined,
        is_discontinued: filters.is_discontinued !== null ? filters.is_discontinued : undefined,
        factory_in_stock: filters.factory_in_stock !== null ? filters.factory_in_stock : undefined,
        sort_by: sortConfig.key || undefined,
        sort_direction: sortConfig.direction || undefined,
      };
      const res = await productsApi.getAll(params);
      const allProducts = res.data?.items || [];

      if (!allProducts.length) {
        toast.error('Нет товаров для экспорта');
        return;
      }

      // Собираем все уникальные имена поставщиков с ценами по всей выборке
      const supplierPriceNames = [];
      allProducts.forEach(p => {
        (p.prices_by_supplier || []).forEach(sp => {
          if (sp.supplier_name && !supplierPriceNames.includes(sp.supplier_name)) {
            supplierPriceNames.push(sp.supplier_name);
          }
        });
      });
      supplierPriceNames.sort();

      const headers = ['ID', 'Артикул', 'Название', 'Бренд', 'Коллекция', 'Поставщик', 'Остаток (шт)', 'Фабрика (наличие)', 'Кол-во на фабрике', 'GUID 1С', 'Активен', 'На сайте', ...supplierPriceNames.map(n => `Цена: ${n}`)];
      const rows = allProducts.map(p => {
        const suppliersWithStock = (p.stocks_by_supplier || [])
          .filter(s => s.quantity > 0)
          .map(s => s.supplier_name || '')
          .filter(Boolean)
          .join(', ');
        const priceMap = {};
        (p.prices_by_supplier || []).forEach(sp => {
          if (sp.supplier_name) priceMap[sp.supplier_name] = sp.price ?? '';
        });
        const fs = p.factory_stock;
        const factoryInStock = fs == null ? '' : fs.in_stock ? 'Да' : 'Нет';
        const factoryQty = fs?.quantity != null ? formatFactoryQty(fs.quantity, p.brand_id) : '';
        return [
          p.id,
          p.sku || '',
          (p.name || '').replace(/"/g, '""'),
          (p.brand_name || '').replace(/"/g, '""'),
          (p.collection_name || '').replace(/"/g, '""'),
          suppliersWithStock,
          `${p.total_stock ?? 0} шт`,
          factoryInStock,
          factoryQty,
          p.guid_1c || '',
          p.is_active ? 'Да' : 'Нет',
          p.website_linked ? 'Да' : 'Нет',
          ...supplierPriceNames.map(n => priceMap[n] ?? ''),
        ];
      });

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(';'))
        .join('\n');

      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.download = `products_${date}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Экспортировано ${allProducts.length} товаров`);
    } catch (e) {
      toast.error('Ошибка экспорта CSV');
    }
  }, [filters, sortConfig]);

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  // Обработчики для перетаскивания колонок
  const handleDragStart = (e, columnKey) => {
    setDraggingColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (draggingColumn && draggingColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    if (draggingColumn && draggingColumn !== targetColumn) {
      const newOrder = [...columnOrder];
      const dragIndex = newOrder.indexOf(draggingColumn);
      const targetIndex = newOrder.indexOf(targetColumn);
      
      // Удаляем из старой позиции
      newOrder.splice(dragIndex, 1);
      // Вставляем в новую позицию
      newOrder.splice(targetIndex, 0, draggingColumn);
      
      setColumnOrder(newOrder);
    }
    setDraggingColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggingColumn(null);
    setDragOverColumn(null);
  };

  // Закрытие модального окна при клике вне его
  const closeProductModal = () => {
    setSelectedProduct(null);
    setEditValues(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showColumnSettings && !e.target.closest('.column-settings-container')) {
        setShowColumnSettings(false);
      }
      if (showSettingsMenu && !e.target.closest('.settings-menu-container')) {
        setShowSettingsMenu(false);
      }
      if (selectedProduct && !e.target.closest('.product-detail-modal') && !e.target.closest('tr')) {
        closeProductModal();
      }
      if (showBulkMenu && !e.target.closest('.bulk-menu-container')) {
        setShowBulkMenu(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedProduct) { closeProductModal(); return; }
        if (showColumnSettings) { setShowColumnSettings(false); return; }
        if (showBulkMenu) { setShowBulkMenu(false); return; }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showColumnSettings, showSettingsMenu, selectedProduct, showBulkMenu]);

  // Debounce поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Статические данные - загружаются ОДИН РАЗ при монтировании
  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await brandsApi.getAll();
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 минут
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await suppliersApi.getAll();
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 минут
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { data: factories = [] } = useQuery({
    queryKey: ['factories'],
    queryFn: async () => {
      const res = await factoriesApi.getAll();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: async () => {
      const res = await collectionsApi.getAll();
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 минут
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const getProductFactory = (brandId) => {
    const brand = brands?.find(b => b.id === brandId);
    return factories?.find(f => f.id === brand?.factory_id) || null;
  };

  // Рассчитать ожидаемую дату доставки от поставщика.
  // Считает рабочие дни (пн-пт). Учитывает время заказа и дни доставки.
  const calcDeliveryDate = (supplier) => {
    if (!supplier?.delivery_day_count_min) return null;

    // Текущее время по МСК (UTC+3)
    const now = new Date();
    const moscowNow = new Date(now.getTime() + (3 * 60 + now.getTimezoneOffset()) * 60000);

    // Стартовая дата: сегодня если до cutoff, иначе завтра
    let start = new Date(moscowNow);
    start.setHours(0, 0, 0, 0);
    if (supplier.order_time) {
      const [h, m] = supplier.order_time.split(':').map(Number);
      const cutoff = new Date(moscowNow);
      cutoff.setHours(h, m, 0, 0);
      if (moscowNow > cutoff) start.setDate(start.getDate() + 1);
    }

    // Добавляем delivery_day_count_min рабочих дней (пн-пт)
    let result = new Date(start);
    let added = 0;
    while (added < supplier.delivery_day_count_min) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay(); // 0=вс, 6=сб
      if (dow !== 0 && dow !== 6) added++;
    }

    // Если заданы конкретные дни доставки — найти ближайший подходящий
    if (supplier.delivery_days?.length > 0) {
      // delivery_days: [1..7], 1=пн, 7=вс → JS: 1=пн, 0=вс
      const validDows = new Set(supplier.delivery_days.map(d => d === 7 ? 0 : d));
      for (let i = 0; i < 14; i++) {
        if (validDows.has(result.getDay())) break;
        result.setDate(result.getDate() + 1);
      }
    }

    return result;
  };

  const fmtDeliveryDate = (date) => {
    if (!date) return null;
    const days = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
    return `${days[date.getDay()]} ${date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`;
  };

  const formatFactoryQty = (qty, brandId) => {
    const factory = getProductFactory(brandId);
    if (factory?.integration_type === 'khroma') {
      if (qty >= 20) return 'больше 20';
      if (qty >= 10) return '10–20';
      if (qty >= 5) return '5–10';
      return '1–5';
    }
    if (factory?.integration_type === 'texdecor') return `${qty} ML`;
    return qty;
  };

  // Товары - с ручным контролем загрузки
  const productsQuery = useQuery({
    queryKey: ['products', page, pageSize, filters, sortConfig],
    queryFn: async () => {
      setIsLoading(true);
      try {
        const params = {
          skip: (page - 1) * pageSize,
          limit: pageSize,
          search: filters.search || undefined,
          brand_id: filters.brand_id || undefined,
          collection_id: filters.collection_id || undefined,
          supplier_id: filters.supplier_id || undefined,
          factory_id: filters.factory_id || undefined,
          in_stock: filters.in_stock || undefined,
          not_synced_to_website: filters.not_synced_to_website || undefined,
          has_price: filters.has_price !== null ? filters.has_price : undefined,
          price_mismatch: filters.price_mismatch !== null ? filters.price_mismatch : undefined,
          website_linked: filters.website_linked !== null ? filters.website_linked : undefined,
          is_discontinued: filters.is_discontinued !== null ? filters.is_discontinued : undefined,
          factory_in_stock: filters.factory_in_stock !== null ? filters.factory_in_stock : undefined,
          sort_by: sortConfig.key || undefined,
          sort_direction: sortConfig.direction || undefined,
        };
        const res = await productsApi.getAll(params);
        return res.data;
      } finally {
        setIsLoading(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Статистика по брендам/коллекциям для сайдбара — те же фильтры, без brand_id/collection_id
  const brandStatsQuery = useQuery({
    queryKey: ['brandStats', filters],
    queryFn: async () => {
      const params = {
        search: filters.search || undefined,
        supplier_id: filters.supplier_id || undefined,
        in_stock: filters.in_stock || undefined,
        not_synced_to_website: filters.not_synced_to_website || undefined,
        has_price: filters.has_price !== null ? filters.has_price : undefined,
        price_mismatch: filters.price_mismatch !== null ? filters.price_mismatch : undefined,
        website_linked: filters.website_linked !== null ? filters.website_linked : undefined,
        is_discontinued: filters.is_discontinued !== null ? filters.is_discontinued : undefined,
        factory_in_stock: filters.factory_in_stock !== null ? filters.factory_in_stock : undefined,
      };
      const res = await productsApi.getBrandStats(params);
      return res.data;
    },
    enabled: isSidebarOpen,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const products = productsQuery.data?.items || [];
  const totalProducts = productsQuery.data?.total || 0;
  const totalPages = Math.ceil(totalProducts / pageSize);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} style={{ opacity: 0.3, marginLeft: '4px' }} />;
    }
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={14} style={{ marginLeft: '4px' }} />
      : <ArrowDown size={14} style={{ marginLeft: '4px' }} />;
  };

  // Конфигурация колонок
  const columnConfig = {
    sku: { label: 'Артикул', sortKey: 'sku' },
    name: { label: 'Название', sortKey: 'name', minWidth: '200px' },
    brand_name: { label: 'Бренд', sortKey: 'brand' },
    collection_name: { label: 'Коллекция', sortKey: 'collection' },
    price: { label: 'Цена', sortKey: 'price', minWidth: '125px' },
    total_stock: { label: 'Остаток', sortKey: 'total_stock' },
    created_at: { label: 'Дата создания', sortKey: 'created_at' },
    last_stock_updated: { label: 'Обновлен остаток', sortKey: 'updated_at' },
    last_stock_website_sync: { label: 'Выгружен остаток', sortKey: 'last_stock_website_sync' },
    last_price_updated: { label: 'Обновлена цена', sortKey: 'last_price_updated' },
    last_price_website_sync: { label: 'Выгружена цена', sortKey: 'last_price_website_sync' },
    website_linked: { label: 'На сайте', sortKey: 'website_linked', align: 'center' },
    is_discontinued: { label: 'Снято', sortKey: 'is_discontinued', align: 'center' },
    factory_in_stock: { label: 'На фабрике', sortKey: null, align: 'center' },
  };

  // Рендер заголовка колонки с возможностью перетаскивания
  const renderColumnHeader = (columnKey) => {
    if (!visibleColumns[columnKey]) return null;
    
    const config = columnConfig[columnKey];
    if (!config) return null;
    const isDragging = draggingColumn === columnKey;
    const isDragOver = dragOverColumn === columnKey;

    return (
      <th
        key={columnKey}
        draggable
        onDragStart={(e) => handleDragStart(e, columnKey)}
        onDragOver={(e) => handleDragOver(e, columnKey)}
        onDrop={(e) => handleDrop(e, columnKey)}
        onDragEnd={handleDragEnd}
        onClick={config.sortKey ? () => handleSort(config.sortKey) : undefined}
        style={{
          padding: '12px 16px',
          textAlign: 'left',
          fontWeight: '600',
          fontSize: '13px',
          color: '#6b7280',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          borderBottom: '1px solid #e5e7eb',
          whiteSpace: 'nowrap',
          minWidth: config.minWidth,
          backgroundColor: isDragging ? '#dbeafe' : isDragOver ? '#f3f4f6' : 'transparent',
          opacity: isDragging ? 0.5 : 1,
          transition: 'background-color 0.15s, opacity 0.15s',
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: config.align === 'center' ? 'center' : 'flex-start',
          pointerEvents: config.sortKey ? 'auto' : 'none'
        }}>
          {config.label}
          {config.sortKey && <SortIcon columnKey={config.sortKey} />}
        </div>
      </th>
    );
  };

  // Рендер ячейки таблицы в том же порядке что и заголовки
  const renderCell = (columnKey, product) => {
    if (!visibleColumns[columnKey]) return null;

    const cellStyle = {
      padding: '12px 16px',
      fontSize: '14px',
    };

    switch (columnKey) {
      case 'sku':
        return <td key={columnKey} style={{ ...cellStyle, color: '#374151', fontFamily: 'monospace' }}>{product.sku}</td>;
      
      case 'name':
        return <td key={columnKey} style={{ ...cellStyle, color: '#111827', fontWeight: '500' }}>{product.name}</td>;
      
      case 'brand_name':
        return <td key={columnKey} style={{ ...cellStyle, color: '#6b7280' }}>{product.brand_name || '-'}</td>;
      
      case 'collection_name':
        return <td key={columnKey} style={{ ...cellStyle, color: '#6b7280' }}>{product.collection_name || '-'}</td>;
      
      case 'price':
        return (
          <td key={columnKey} style={{ ...cellStyle, color: '#111827', fontWeight: '600' }}>
            {product.max_price > 0 ? `${product.max_price.toFixed(2)} ₽` : '-'}
          </td>
        );
      
      case 'total_stock':
        return (
          <td key={columnKey} style={{ ...cellStyle }}>
            <span style={{
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              backgroundColor: product.total_stock > 0 ? '#dcfce7' : '#fee2e2',
              color: product.total_stock > 0 ? '#15803d' : '#dc2626'
            }}>
              {product.total_stock || 0}
            </span>
          </td>
        );
      
      case 'created_at':
        return (
          <td key={columnKey} style={{ ...cellStyle, color: '#6b7280', fontSize: '13px' }}>
            {product.created_at ? new Date(product.created_at).toLocaleDateString('ru-RU') : '-'}
          </td>
        );
      
      case 'last_stock_updated':
        return (
          <td key={columnKey} style={{ ...cellStyle, color: '#6b7280', fontSize: '13px' }}>
            {product.last_stock_updated ? new Date(product.last_stock_updated).toLocaleString('ru-RU') : '-'}
          </td>
        );
      
      case 'last_stock_website_sync':
        return (
          <td key={columnKey} style={{ ...cellStyle, color: '#6b7280', fontSize: '13px' }}>
            {product.last_stock_website_sync ? new Date(product.last_stock_website_sync).toLocaleString('ru-RU') : '-'}
          </td>
        );
      
      case 'last_price_updated':
        return (
          <td key={columnKey} style={{ ...cellStyle, color: '#6b7280', fontSize: '13px' }}>
            {product.last_price_updated ? new Date(product.last_price_updated).toLocaleString('ru-RU') : '-'}
          </td>
        );
      
      case 'last_price_website_sync':
        return (
          <td key={columnKey} style={{ ...cellStyle, color: '#6b7280', fontSize: '13px' }}>
            {product.last_price_website_sync ? new Date(product.last_price_website_sync).toLocaleString('ru-RU') : '-'}
          </td>
        );
      
      case 'website_linked':
        return (
          <td key={columnKey} style={{ ...cellStyle, textAlign: 'center' }}>
            <span style={{
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: product.website_linked ? '#dcfce7' : '#fee2e2',
              color: product.website_linked ? '#15803d' : '#dc2626'
            }}>
              {product.website_linked ? 'Да' : 'Нет'}
            </span>
          </td>
        );

      case 'is_discontinued':
        return (
          <td key={columnKey} style={{ ...cellStyle, textAlign: 'center' }}>
            {product.is_discontinued ? (
              <span style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: '#fef3c7',
                color: '#92400e'
              }}>
                Снято
              </span>
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
            )}
          </td>
        );

      case 'factory_in_stock':
        return (
          <td key={columnKey} style={{ ...cellStyle, textAlign: 'center' }}>
            {product.factory_in_stock === null || product.factory_in_stock === undefined ? (
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
            ) : product.factory_in_stock ? (
              <span style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: '#dcfce7',
                color: '#15803d'
              }}>
                Есть
              </span>
            ) : (
              <span style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}>
                Нет
              </span>
            )}
          </td>
        );

      default:
        return <td key={columnKey} style={cellStyle}>-</td>;
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => productsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Товар удален');
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (productIds) => productsApi.bulkDelete(productIds),
    onSuccess: (res) => {
      toast.success(`Удалено товаров: ${res.data.deleted}`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedProducts([]);
    },
    onError: (error) => {
      toast.error(`Ошибка удаления: ${error.response?.data?.detail || error.message}`);
    },
  });

  const bulkDeactivateMutation = useMutation({
    mutationFn: (productIds) => productsApi.bulkDeactivate(productIds),
    onSuccess: (res) => {
      toast.success(`Деактивировано товаров: ${res.data.deactivated}`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedProducts([]);
    },
    onError: (error) => {
      toast.error(`Ошибка деактивации: ${error.response?.data?.detail || error.message}`);
    },
  });

  const sendSelectedRemainsToWebsiteMutation = useMutation({
    mutationFn: (productIds) => tasksApi.sendRemainsSelectedProducts(productIds),
    onSuccess: (res) => {
      toast.success(`Остатки выбранных товаров отправлены. Task ID: ${res.data.task_id}`);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setSelectedProducts([]);
    },
    onError: (error) => {
      toast.error(`Ошибка отправки остатков: ${error.response?.data?.detail || error.message}`);
    },
  });

  const sendSelectedPricesToWebsiteMutation = useMutation({
    mutationFn: (productIds) => tasksApi.sendPricesSelectedProducts(productIds),
    onSuccess: (res) => {
      toast.success(`Цены выбранных товаров отправлены. Task ID: ${res.data.task_id}`);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setSelectedProducts([]);
    },
    onError: (error) => {
      toast.error(`Ошибка отправки цен: ${error.response?.data?.detail || error.message}`);
    },
  });

  const bulkZeroPricesMutation = useMutation({
    mutationFn: (productIds) => productsApi.bulkZeroPrices(productIds),
    onSuccess: (res) => {
      toast.success(`Обнулены цены у ${res.data.zeroed_products} товар(ов)`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedProducts([]);
    },
    onError: (error) => {
      toast.error(`Ошибка: ${error.response?.data?.detail || error.message}`);
    },
  });

  const bulkZeroStocksMutation = useMutation({
    mutationFn: (productIds) => productsApi.bulkZeroStocks(productIds),
    onSuccess: (res) => {
      toast.success(`Обнулены остатки у ${res.data.zeroed_products} товар(ов)`);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedProducts([]);
    },
    onError: (error) => {
      toast.error(`Ошибка: ${error.response?.data?.detail || error.message}`);
    },
  });

  const updateProductManualMutation = useMutation({
    mutationFn: ({ id, data }) => productsApi.updateManual(id, data),
    onSuccess: (res) => {
      toast.success('Товар сохранён');
      setSelectedProduct(res.data);
      setEditValues(null);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error) => {
      toast.error(`Ошибка сохранения: ${error.response?.data?.detail || error.message}`);
    },
  });

  const toggleSelectProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSendSelectedToWebsite = () => {
    if (selectedProducts.length === 0) {
      toast.error('Не выбраны товары');
      return;
    }
    if (window.confirm(`Отправить остатки ${selectedProducts.length} товара(ов) на сайт?`)) {
      sendSelectedRemainsToWebsiteMutation.mutate(selectedProducts);
    }
  };

  const handleSendSelectedPricesToWebsite = () => {
    if (selectedProducts.length === 0) {
      toast.error('Не выбраны товары');
      return;
    }
    if (window.confirm(`Отправить цены ${selectedProducts.length} товара(ов) на сайт?`)) {
      sendSelectedPricesToWebsiteMutation.mutate(selectedProducts);
    }
  };

  const handleSelectAll = async () => {
    // Если уже выделены все из выборки, снимаем выделение
    if (selectedProducts.length === totalProducts) {
      setSelectedProducts([]);
      return;
    }

    // Получаем все ID из текущей выборки через специальный эндпоинт
    try {
      const params = {
        search: filters.search || undefined,
        brand_id: filters.brand_id || undefined,
        collection_id: filters.collection_id || undefined,
        supplier_id: filters.supplier_id || undefined,
        in_stock: filters.in_stock || undefined,
        not_synced_to_website: filters.not_synced_to_website || undefined,
      };
      const res = await productsApi.getAllIds(params);
      setSelectedProducts(res.data.ids);
      toast.success(`Выделено ${res.data.ids.length} товаров`);
    } catch (error) {
      toast.error('Ошибка выделения всех товаров');
    }
  };

  const handleSelectBrand = useCallback((brandId) => {
    setFilters(prev => ({ ...prev, brand_id: brandId || '', collection_id: '' }));
    setPage(1);
  }, []);

  const handleSelectCollection = useCallback((collectionId) => {
    setFilters(prev => ({ ...prev, collection_id: collectionId || '' }));
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setFilters(prev => ({ ...prev, search: '' }));
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleResetAll = useCallback(() => {
    setSearchInput('');
    setFilters({
      search: '',
      brand_id: '',
      collection_id: '',
      supplier_id: '',
      factory_id: '',
      in_stock: false,
      not_synced_to_website: false,
      has_price: null,
      price_mismatch: null,
      website_linked: null,
      is_discontinued: null,
      factory_in_stock: null,
    });
    setPage(1);
  }, []);

  if (productsQuery.isError) {
    return (
      <div className="card">
        <h2>Ошибка</h2>
        <p>{productsQuery.error?.message || 'Произошла ошибка'}</p>
      </div>
    );
  }

  return (
    <>
      {isLoading && <LoadingBar />}
      
      <div style={{ display: 'flex', height: 'calc(100vh - 52px)', overflow: 'hidden', margin: '-20px' }}>
        {/* Дерево брендов и коллекций - скрываемая панель */}
        {isSidebarOpen && (
          <BrandCollectionTree
            brands={brands}
            collections={collections}
            selectedBrandId={filters.brand_id}
            selectedCollectionId={filters.collection_id}
            onSelectBrand={handleSelectBrand}
            onSelectCollection={handleSelectCollection}
            brandCounts={brandStatsQuery.data?.brands}
            collectionCounts={brandStatsQuery.data?.collections}
          />
        )}

        {/* Основной контент */}
        <div style={{ flex: 1, overflow: 'auto', paddingLeft: '24px', height: '100vh', backgroundColor: '#f9fafb' }}>
          <div className="card" style={{ padding: '24px' }}>
            {/* Заголовок и действия — одна компактная строка */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px' }}>
              {/* Левая часть: переключатель сайдбара + название */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  title={isSidebarOpen ? 'Скрыть бренды и коллекции' : 'Показать бренды и коллекции'}
                  style={{ padding: '7px', backgroundColor: isSidebarOpen ? '#3b82f6' : '#fff', color: isSidebarOpen ? '#fff' : '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                >
                  <Menu size={17} />
                </button>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#111827', whiteSpace: 'nowrap' }}>Товары</span>
              </div>

              {/* Правая часть: действия */}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>

                {/* Выделить все */}
                <button onClick={handleSelectAll}
                  style={{ height: '36px', padding: '0 10px', backgroundColor: selectedProducts.length === totalProducts && totalProducts > 0 ? '#f3f4f6' : '#fff', border: '1px solid #e5e7eb', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={selectedProducts.length === totalProducts && totalProducts > 0} readOnly style={{ cursor: 'pointer', width: '14px', height: '14px', margin: 0 }} />
                  {selectedProducts.length > 0 ? `${selectedProducts.length}` : 'Выбрать'}
                </button>

                {/* Действия (дропдаун) */}
                {selectedProducts.length > 0 && (
                  <div style={{ position: 'relative' }} className="bulk-menu-container">
                    <button onClick={() => setShowBulkMenu(v => !v)}
                      style={{ height: '36px', padding: '0 12px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      ⚡ Действия ▾
                    </button>
                    {showBulkMenu && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 2000, minWidth: '230px', overflow: 'hidden' }}>
                        {[
                          { icon: '📤', label: 'Отправить остатки на сайт', color: '#3b82f6', action: () => { setShowBulkMenu(false); if (window.confirm(`Отправить остатки ${selectedProducts.length} товар(ов) на сайт?`)) sendSelectedRemainsToWebsiteMutation.mutate(selectedProducts); } },
                          { icon: '💰', label: 'Отправить цены на сайт',    color: '#10b981', action: () => { setShowBulkMenu(false); if (window.confirm(`Отправить цены ${selectedProducts.length} товар(ов) на сайт?`)) sendSelectedPricesToWebsiteMutation.mutate(selectedProducts); } },
                          { icon: '🔕', label: 'Деактивировать',            color: '#f59e0b', action: () => { setShowBulkMenu(false); if (window.confirm(`Деактивировать ${selectedProducts.length} товар(ов)?`)) bulkDeactivateMutation.mutate(selectedProducts); } },
                          { icon: '💲', label: 'Обнулить цены',             color: '#f97316', action: () => { setShowBulkMenu(false); if (window.confirm(`Обнулить цены у ${selectedProducts.length} товар(ов)?`)) bulkZeroPricesMutation.mutate(selectedProducts); } },
                          { icon: '📦', label: 'Обнулить остатки',          color: '#8b5cf6', action: () => { setShowBulkMenu(false); if (window.confirm(`Обнулить остатки у ${selectedProducts.length} товар(ов)?`)) bulkZeroStocksMutation.mutate(selectedProducts); } },
                          { icon: '🗑',  label: 'Удалить навсегда',         color: '#ef4444', action: () => { setShowBulkMenu(false); if (window.confirm(`Удалить ${selectedProducts.length} товар(ов) НАВСЕГДА?`)) bulkDeleteMutation.mutate(selectedProducts); } },
                        ].map(({ icon, label, color, action }) => (
                          <button key={label} onClick={action}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '13px', color, fontWeight: '500', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <span style={{ width: '18px' }}>{icon}</span>{label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Настройки: CSV + Колонки */}
                <div style={{ position: 'relative' }} className="settings-menu-container">
                  <button onClick={() => setShowSettingsMenu(v => !v)}
                    style={{ height: '36px', padding: '0 10px', backgroundColor: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '500' }}>
                    <Settings size={15} />
                    <span style={{ fontSize: '9px', opacity: 0.5 }}>{showSettingsMenu ? '▲' : '▼'}</span>
                  </button>

                  {showSettingsMenu && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', padding: '12px', minWidth: '200px', zIndex: 1000 }}>
                      {/* CSV */}
                      <button onClick={() => { setShowSettingsMenu(false); handleExportCsv(); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: '500', textAlign: 'left', borderRadius: '6px 6px 0 0', marginBottom: '4px' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <Download size={14} /> Экспорт CSV
                      </button>

                      {/* Колонки */}
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.07em', textTransform: 'uppercase', padding: '6px 10px 4px' }}>Колонки</div>
                      {[
                        { key: 'sku', label: 'Артикул' },
                        { key: 'name', label: 'Название' },
                        { key: 'brand_name', label: 'Бренд' },
                        { key: 'collection_name', label: 'Коллекция' },
                        { key: 'price', label: 'Цена' },
                        { key: 'total_stock', label: 'Остаток' },
                        { key: 'created_at', label: 'Дата создания' },
                        { key: 'last_stock_updated', label: 'Обновлён остаток' },
                        { key: 'last_stock_website_sync', label: 'Выгружен остаток' },
                        { key: 'last_price_updated', label: 'Обновлена цена' },
                        { key: 'last_price_website_sync', label: 'Выгружена цена' },
                        { key: 'website_linked', label: 'На сайте' },
                        { key: 'is_discontinued', label: 'Снято с пр-ва' },
                        { key: 'factory_in_stock', label: 'На фабрике' },
                      ].map(col => (
                        <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', cursor: 'pointer', borderRadius: '5px' }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <input type="checkbox" checked={visibleColumns[col.key]} onChange={() => toggleColumn(col.key)} style={{ cursor: 'pointer', width: '14px', height: '14px' }} />
                          <span style={{ fontSize: '13px', color: '#374151' }}>{col.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>

            <ProductsFilters
              searchInput={searchInput}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetAll={handleResetAll}
              suppliers={suppliers}
              collections={collections}
              factories={factories}
              totalProducts={totalProducts}
            />

            {/* Таблица */}
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: '#fff'
              }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={{ 
                      width: '40px', 
                      padding: '12px 16px', 
                      textAlign: 'left',
                      fontWeight: '600',
                      fontSize: '13px',
                      color: '#6b7280',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === products.length && products.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProducts(products.map(p => p.id));
                          } else {
                            setSelectedProducts([]);
                          }
                        }}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </th>
                    {/* Рендерим колонки в порядке из columnOrder с возможностью перетаскивания */}
                    {columnOrder.map(columnKey => renderColumnHeader(columnKey))}
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center',
                      fontWeight: '600',
                      fontSize: '13px',
                      color: '#6b7280',
                      borderBottom: '1px solid #e5e7eb',
                      width: '80px'
                    }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="100" style={{ padding: '60px 0', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#9ca3af' }}>
                          <div style={{
                            width: '32px', height: '32px',
                            border: '3px solid #e5e7eb',
                            borderTopColor: '#3b82f6',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite'
                          }} />
                          <span style={{ fontSize: '14px' }}>Поиск...</span>
                        </div>
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td colSpan="100" style={{ padding: '60px 0', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#9ca3af' }}>
                          <span style={{ fontSize: '32px' }}>🔍</span>
                          <span style={{ fontSize: '15px', fontWeight: '500', color: '#6b7280' }}>Ничего не найдено</span>
                          <span style={{ fontSize: '13px' }}>Попробуйте изменить параметры поиска или сбросить фильтры</span>
                        </div>
                      </td>
                    </tr>
                  ) : products.map(product => (
                    <tr 
                      key={product.id} 
                      onClick={(e) => {
                        // Не открываем модалку если кликнули по чекбоксу или кнопке удаления
                        if (e.target.type === 'checkbox' || e.target.closest('button')) {
                          return;
                        }
                        setSelectedProduct(product);
                        setShowWebsiteImage(false);
                        setEditValues(null);
                      }}
                      style={{ 
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => toggleSelectProduct(product.id)}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                      </td>
                      {/* Рендерим ячейки в порядке из columnOrder */}
                      {columnOrder.map(columnKey => renderCell(columnKey, product))}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => deleteMutation.mutate(product.id)}
                            disabled={deleteMutation.isLoading}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#fee',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {totalProducts > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginTop: '20px',
                marginBottom: '30px',
                padding: '16px 0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Страница <span style={{ fontWeight: '600', color: '#374151' }}>{page}</span> из <span style={{ fontWeight: '600', color: '#374151' }}>{totalPages || 1}</span>
                    <span style={{ marginLeft: '12px' }}>({totalProducts} товаров)</span>
                  </div>
                  {/* Выбор количества на странице */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#6b7280' }}>
                    По
                    <select
                      value={pageSize}
                      onChange={e => handlePageSizeChange(Number(e.target.value))}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {[25, 50, 100, 200, 500].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    на странице
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: page === 1 ? '#f3f4f6' : '#fff',
                      color: page === 1 ? '#9ca3af' : '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || totalPages === 0}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: page === totalPages ? '#f3f4f6' : '#fff',
                      color: page === totalPages ? '#9ca3af' : '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Модальное окно с деталями товара */}
      {selectedProduct && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px' }}
          onClick={closeProductModal}
        >
          <div
            className="product-detail-modal"
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '12px', maxWidth: '860px', width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
          >
            {/* ── Шапка ── */}
            {(() => {
              const p = selectedProduct;
              const fmtShort = v => v ? new Date(v).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
              const badge = (text, bg, color) => (
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '999px', backgroundColor: bg, color, whiteSpace: 'nowrap' }}>{text}</span>
              );
              const sectionLabel = text => (
                <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '10px' }}>{text}</div>
              );

              return (
                <>
                  {/* Шапка */}
                  <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '17px', fontWeight: '700', color: '#111827', lineHeight: 1.35, marginBottom: '5px' }}>{p.name}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6b7280' }}>
                          <code style={{ fontFamily: 'monospace', fontWeight: '600', color: '#374151', fontSize: '13px' }}>{p.sku}</code>
                          {p.brand_name && <span style={{ color: '#d1d5db' }}>·</span>}
                          {p.brand_name && <span>{p.brand_name}</span>}
                          {p.collection_name && <span style={{ color: '#d1d5db' }}>·</span>}
                          {p.collection_name && <span>{p.collection_name}</span>}
                          {p.website_url && (
                            <>
                              <span style={{ color: '#d1d5db' }}>·</span>
                              <a href={p.website_url} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                ↗ Открыть на сайте
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {editValues
                          ? <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: editValues.is_active ? '#15803d' : '#dc2626' }}>
                              <input type="checkbox" checked={editValues.is_active} onChange={e => setEditValues(v => ({ ...v, is_active: e.target.checked }))} style={{ width: '14px', height: '14px' }} />
                              {editValues.is_active ? 'Активен' : 'Неактивен'}
                            </label>
                          : badge(p.is_active ? 'Активен' : 'Неактивен', p.is_active ? '#dcfce7' : '#fee2e2', p.is_active ? '#15803d' : '#dc2626')
                        }
                        {badge(p.website_linked ? 'На сайте' : 'Не на сайте', p.website_linked ? '#dbeafe' : '#f3f4f6', p.website_linked ? '#1d4ed8' : '#6b7280')}
                        {badge(p.is_discontinued ? 'Снято с пр-ва' : 'В производстве', p.is_discontinued ? '#fef3c7' : '#f0fdf4', p.is_discontinued ? '#92400e' : '#166534')}
                        <button onClick={closeProductModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '22px', lineHeight: 1, padding: '0 2px', marginLeft: '4px' }}>×</button>
                      </div>
                    </div>
                  </div>

                  {/* Тело */}
                  <div style={{ overflow: 'auto', flex: 1 }}>

                    {/* ── Замена ── */}
                    {p.replaced_by_id && (
                      <div style={{ padding: '10px 22px', background: '#fef3c7', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '15px' }}>⚠️</span>
                        <span style={{ fontSize: '13px', color: '#92400e' }}>
                          Артикул снят с производства. Замена:{' '}
                          <button
                            onClick={async () => {
                              try {
                                const res = await productsApi.getById(p.replaced_by_id);
                                setSelectedProduct(res.data);
                                setEditValues(null);
                              } catch {}
                            }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#92400e', textDecoration: 'underline', fontFamily: 'monospace', fontSize: '13px', fontWeight: '700' }}
                          >
                            {p.replacement_sku || `#${p.replaced_by_id}`}
                          </button>
                        </span>
                      </div>
                    )}

                    {/* ── Цены ── */}
                    <div style={{ padding: '16px 22px', borderBottom: '1px solid #f3f4f6' }}>
                      {sectionLabel('Цены')}
                      {p.prices_by_supplier && p.prices_by_supplier.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {p.prices_by_supplier.map((pi, idx) => {
                            const is1c = pi.supplier_id === null;
                            const isMax = pi.price === p.max_price;
                            const hasCurrency = pi.currency && pi.currency !== 'RUB';
                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '140px' }}>{pi.supplier_name}</span>
                                {is1c && editValues ? (
                                  <input type="number" min="0" step="0.01" value={editValues.price_1c}
                                    onChange={e => setEditValues(v => ({ ...v, price_1c: parseFloat(e.target.value) || 0 }))}
                                    style={{ width: '100px', textAlign: 'right', border: '1px solid #3b82f6', borderRadius: '4px', padding: '2px 6px', fontSize: '14px', fontWeight: '600', color: '#059669' }} />
                                ) : (
                                  <span style={{ fontSize: isMax ? '16px' : '14px', fontWeight: isMax ? '700' : '500', color: isMax ? '#059669' : '#374151' }}>
                                    {pi.price} ₽
                                  </span>
                                )}
                                {hasCurrency && pi.price_original != null && (
                                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>{pi.price_original} {pi.currency}</span>
                                )}
                                {isMax && <span style={{ fontSize: '10px', fontWeight: '600', color: '#059669', background: '#f0fdf4', padding: '1px 6px', borderRadius: '4px' }}>макс</span>}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '13px', color: '#9ca3af' }}>Нет данных</span>
                          {editValues && (
                            <input type="number" min="0" step="0.01" placeholder="Цена 1С" value={editValues.price_1c}
                              onChange={e => setEditValues(v => ({ ...v, price_1c: parseFloat(e.target.value) || 0 }))}
                              style={{ width: '120px', border: '1px solid #3b82f6', borderRadius: '4px', padding: '4px 8px', fontSize: '14px' }} />
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Остатки у поставщиков ── */}
                    {p.stocks_by_supplier && p.stocks_by_supplier.length > 0 && (
                      <div style={{ padding: '16px 22px', borderBottom: '1px solid #f3f4f6' }}>
                        {sectionLabel(`Остатки у поставщиков · итого ${p.total_stock ?? 0}`)}
                        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                              {['Поставщик', 'Кол-во', 'Резерв', 'Доступно', 'Обновлено', 'Доставка'].map((h, i) => (
                                <th key={i} style={{ padding: '4px 8px 8px', textAlign: i === 0 ? 'left' : 'right', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {p.stocks_by_supplier.map((stock, idx) => {
                              const editStock = editValues?.stocks?.find(s => s.stock_id === stock.stock_id);
                              const qty = editStock ? editStock.quantity : stock.quantity;
                              return (
                                <tr key={idx} style={{ borderBottom: idx < p.stocks_by_supplier.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                                  <td style={{ padding: '7px 8px', color: '#374151', fontWeight: '500' }}>{stock.supplier_name}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                    {editValues ? (
                                      <input type="number" min="0" step="1" value={qty}
                                        onChange={e => { const val = parseFloat(e.target.value) || 0; setEditValues(v => ({ ...v, stocks: v.stocks.map(s => s.stock_id === stock.stock_id ? { ...s, quantity: val } : s) })); }}
                                        style={{ width: '72px', textAlign: 'right', border: '1px solid #3b82f6', borderRadius: '4px', padding: '2px 6px', fontSize: '13px', fontWeight: '600', color: qty > 0 ? '#059669' : '#6b7280' }} />
                                    ) : (
                                      <span style={{ fontWeight: '600', color: qty > 0 ? '#059669' : '#9ca3af' }}>{qty}</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '7px 8px', textAlign: 'right', color: '#9ca3af' }}>{stock.reserved || 0}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: '500', color: '#374151' }}>{editStock ? Math.max(0, editStock.quantity - (stock.reserved || 0)) : (stock.available ?? stock.quantity - (stock.reserved || 0))}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>{fmtShort(stock.last_stock_updated)}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'right', fontSize: '12px' }}>
                                    {(() => {
                                      const sup = suppliers?.find(s => s.id === stock.supplier_id);
                                      const d = calcDeliveryDate(sup);
                                      return d
                                        ? <span style={{ color: '#374151', fontWeight: '500' }}>{fmtDeliveryDate(d)}</span>
                                        : <span style={{ color: '#d1d5db' }}>—</span>;
                                    })()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* ── На фабрике ── */}
                    <div style={{ padding: '16px 22px', borderBottom: '1px solid #f3f4f6' }}>
                      {sectionLabel('На фабрике')}
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
                        {/* Наличие */}
                        <div>
                          {editValues ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editValues.factory_stock?.in_stock ?? false}
                                onChange={e => setEditValues(v => ({ ...v, factory_stock: { ...v.factory_stock, in_stock: e.target.checked } }))}
                                style={{ width: '15px', height: '15px' }} />
                              <span style={{ fontSize: '13px', fontWeight: '600', color: editValues.factory_stock?.in_stock ? '#15803d' : '#dc2626' }}>
                                {editValues.factory_stock?.in_stock ? 'Есть' : 'Нет'}
                              </span>
                            </label>
                          ) : (
                            <span style={{ fontSize: '13px', fontWeight: '700', padding: '3px 10px', borderRadius: '6px',
                              backgroundColor: p.factory_stock?.in_stock ? '#dcfce7' : p.factory_stock ? '#fee2e2' : '#f3f4f6',
                              color: p.factory_stock?.in_stock ? '#15803d' : p.factory_stock ? '#dc2626' : '#9ca3af' }}>
                              {p.factory_stock ? (p.factory_stock.in_stock ? 'Есть' : 'Нет') : 'Нет данных'}
                            </span>
                          )}
                        </div>

                        {/* Арт. фабрики */}
                        {p.factory_stock?.factory_sku && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>арт.</span>
                            <code style={{ fontSize: '13px', color: '#374151', fontFamily: 'monospace', background: '#f3f4f6', padding: '1px 6px', borderRadius: '4px' }}>{p.factory_stock.factory_sku}</code>
                          </div>
                        )}

                        {/* Количество */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>кол-во</span>
                          {editValues ? (
                            <input type="number" min="0" step="1" value={editValues.factory_stock?.quantity ?? ''}
                              onChange={e => setEditValues(v => ({ ...v, factory_stock: { ...v.factory_stock, quantity: e.target.value } }))}
                              placeholder="—"
                              style={{ width: '72px', border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 6px', fontSize: '13px', textAlign: 'right' }} />
                          ) : (
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{p.factory_stock?.quantity != null ? formatFactoryQty(p.factory_stock.quantity, p.brand_id) : '—'}</span>
                          )}
                        </div>

                        {/* Партии */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>партия</span>
                          {editValues ? (
                            <input value={editValues.factory_stock?.batch ?? ''} placeholder="—"
                              onChange={e => setEditValues(v => ({ ...v, factory_stock: { ...v.factory_stock, batch: e.target.value } }))}
                              style={{ width: '90px', border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 6px', fontSize: '13px' }} />
                          ) : (() => {
                            const batches = p.factory_stock?.batches;
                            const pFactory = getProductFactory(p.brand_id);
                            const batchUnit = pFactory?.integration_type === 'texdecor' ? 'ML' : 'м';
                            if (batches && batches.length > 0) {
                              return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {batches.map((b, i) => (
                                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                      <span style={{ fontSize: '12px', background: '#f3f4f6', padding: '1px 7px', borderRadius: '4px', color: '#374151', fontWeight: '500' }}>
                                        {b.batch}{b.quantity != null ? ` — ${b.quantity}${batchUnit}` : ''}
                                      </span>
                                      {b.pieces && b.pieces.length > 0 && (
                                        <span style={{ fontSize: '11px', color: '#6b7280', paddingLeft: '4px' }}>
                                          {b.pieces.map((pc, j) => `${pc}${batchUnit}`).join(' + ')}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return <span style={{ fontSize: '13px', color: '#374151' }}>{p.factory_stock?.batch || '—'}</span>;
                          })()}
                        </div>

                        {/* Дата ближайшей поставки */}
                        {!editValues && p.factory_stock?.incoming_date && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>производство</span>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', background: '#fef3c7', padding: '1px 8px', borderRadius: '4px' }}>
                              {new Date(p.factory_stock.incoming_date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                              {p.factory_stock.incoming_quantity ? `, ${Number.isInteger(p.factory_stock.incoming_quantity) ? p.factory_stock.incoming_quantity : p.factory_stock.incoming_quantity.toFixed(1)}` : ''}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Временные метки фабрики */}
                      {p.factory_stock && (
                        <div style={{ display: 'flex', gap: '20px', marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                          <span>обновлено <b style={{ color: '#6b7280' }}>{fmtShort(p.factory_stock.updated_at)}</b></span>
                          {p.factory_stock.changed_at && <span>изменилось <b style={{ color: '#6b7280' }}>{fmtShort(p.factory_stock.changed_at)}</b></span>}
                          {p.factory_stock.notified_at && <span>уведомление <b style={{ color: '#6b7280' }}>{fmtShort(p.factory_stock.notified_at)}</b></span>}
                        </div>
                      )}
                    </div>

                    {/* ── Подробнее (свёрнуто) ── */}
                    <details style={{ padding: '0' }}>
                      <summary style={{ padding: '12px 22px', fontSize: '12px', color: '#9ca3af', cursor: 'pointer', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '10px' }}>▶</span> Дополнительно
                      </summary>
                      <div style={{ padding: '4px 22px 16px', borderTop: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px', fontSize: '13px', marginBottom: '12px' }}>
                          <span style={{ color: '#9ca3af' }}>ID</span><span style={{ color: '#374151' }}>{p.id}</span>
                          <span style={{ color: '#9ca3af' }}>GUID 1С</span><code style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>{p.guid_1c || '—'}</code>
                          {p.normalized_sku && <><span style={{ color: '#9ca3af' }}>Норм. артикул</span><code style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>{p.normalized_sku}</code></>}
                          {p.description && <><span style={{ color: '#9ca3af' }}>Описание</span><span style={{ color: '#374151' }}>{p.description}</span></>}
                          {p.photo_url && <><span style={{ color: '#9ca3af' }}>Фото</span><a href={p.photo_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '12px' }}>Открыть ↗</a></>}
                          <span style={{ color: '#9ca3af' }}>Создан</span><span style={{ color: '#6b7280', fontSize: '12px' }}>{fmtShort(p.created_at)}</span>
                          <span style={{ color: '#9ca3af' }}>Остаток обновлён</span><span style={{ color: '#6b7280', fontSize: '12px' }}>{fmtShort(p.last_stock_updated)}</span>
                          <span style={{ color: '#9ca3af' }}>Остаток выгружен</span><span style={{ color: '#6b7280', fontSize: '12px' }}>{fmtShort(p.last_stock_website_sync)}</span>
                          <span style={{ color: '#9ca3af' }}>Цена обновлена</span><span style={{ color: '#6b7280', fontSize: '12px' }}>{fmtShort(p.last_price_updated)}</span>
                          <span style={{ color: '#9ca3af' }}>Цена выгружена</span><span style={{ color: '#6b7280', fontSize: '12px' }}>{fmtShort(p.last_price_website_sync)}</span>
                        </div>

                        {p.website_image && (
                          <div style={{ marginBottom: '10px' }}>
                            <button onClick={() => setShowWebsiteImage(v => !v)}
                              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontSize: '12px', color: '#6b7280', marginBottom: showWebsiteImage ? '8px' : 0 }}>
                              {showWebsiteImage ? '▲ Скрыть фото' : '▼ Фото с сайта'}
                            </button>
                            {showWebsiteImage && (
                              <a href={p.website_image} target="_blank" rel="noopener noreferrer">
                                <img src={p.website_image} alt="Product" style={{ display: 'block', maxWidth: '180px', maxHeight: '180px', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer' }}
                                  onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="color:#9ca3af;font-size:12px">Недоступно</span>'; }} />
                              </a>
                            )}
                          </div>
                        )}

                        {p.extra_data && (
                          <pre style={{ fontSize: '11px', color: '#6b7280', background: '#f9fafb', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', margin: 0, overflow: 'auto', maxHeight: '160px' }}>
                            {JSON.stringify(p.extra_data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </details>
                  </div>

                  {/* ── Футер ── */}
                  <div style={{ padding: '12px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {!editValues && (
                      <button onClick={() => {
                        const price1c = p.prices_by_supplier?.find(pi => pi.supplier_id === null)?.price ?? p.price ?? 0;
                        setEditValues({ is_active: p.is_active, price_1c: price1c, stocks: (p.stocks_by_supplier || []).map(s => ({ stock_id: s.stock_id, quantity: s.quantity })), factory_stock: { in_stock: p.factory_stock?.in_stock ?? false, batch: p.factory_stock?.batch ?? '', quantity: p.factory_stock?.quantity ?? '' } });
                      }} style={{ padding: '7px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                        Редактировать
                      </button>
                    )}
                    {editValues && (
                      <button onClick={() => updateProductManualMutation.mutate({ id: p.id, data: editValues })}
                        disabled={updateProductManualMutation.isPending}
                        style={{ padding: '7px 18px', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: updateProductManualMutation.isPending ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: updateProductManualMutation.isPending ? 0.7 : 1 }}>
                        {updateProductManualMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                      </button>
                    )}
                    {editValues && (
                      <button onClick={() => setEditValues(null)}
                        style={{ padding: '7px 14px', backgroundColor: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                        Отмена
                      </button>
                    )}
                    <button onClick={closeProductModal}
                      style={{ padding: '7px 14px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                      Закрыть
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsPage;
